import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  showDetails: boolean;
}

/**
 * Error Boundary per evitare schermata nera in produzione.
 * Usa solo HTML/CSS nativi per funzionare anche senza ChakraProvider.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, showDetails: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary]", error?.message || String(error), errorInfo?.componentStack);
    try {
      sessionStorage.setItem(
        "ts_last_error",
        JSON.stringify({
          message: error?.message || String(error),
          stack: error?.stack?.slice(0, 500),
          componentStack: errorInfo?.componentStack?.slice(0, 500),
          url: window.location.href,
        })
      );
    } catch {}
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      let storedError: { message?: string; stack?: string; componentStack?: string } | null = null;
      try {
        const raw = sessionStorage.getItem("ts_last_error");
        if (raw) storedError = JSON.parse(raw);
      } catch {}

      return (
        <div
          style={{
            minHeight: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            backgroundColor: "#f9fafb",
          }}
        >
          <div style={{ maxWidth: "28rem", textAlign: "center" }}>
            <p
              style={{
                fontSize: "1.125rem",
                fontWeight: 600,
                color: "#1f2937",
                marginBottom: "0.75rem",
              }}
            >
              Qualcosa non ha funzionato correttamente
            </p>
            <p
              style={{
                fontSize: "0.875rem",
                color: "#6b7280",
                marginBottom: "1.5rem",
              }}
            >
              Prova a ricaricare la pagina. Se il problema persiste, svuota la cache del browser.
            </p>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={this.handleReload}
                style={{
                  padding: "0.5rem 1rem",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "white",
                  backgroundColor: "#2563eb",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Ricarica la pagina
              </button>
              {storedError?.message && (
                <button
                  type="button"
                  onClick={() => this.setState({ showDetails: !this.state.showDetails })}
                  style={{
                    padding: "0.5rem 1rem",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "#374151",
                    backgroundColor: "#e5e7eb",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  {this.state.showDetails ? "Nascondi dettagli" : "Mostra dettagli"}
                </button>
              )}
            </div>
            {this.state.showDetails && storedError?.message && (
              <pre
                style={{
                  marginTop: "1rem",
                  padding: "0.75rem",
                  fontSize: "0.75rem",
                  textAlign: "left",
                  backgroundColor: "#f3f4f6",
                  borderRadius: "6px",
                  overflow: "auto",
                  maxHeight: "12rem",
                }}
              >
                {storedError.message}
                {storedError.componentStack ? `\n\n${storedError.componentStack}` : ""}
              </pre>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
